import { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { TestIconsWhite, SchwartzIconWhite } from '../data/testsData';
import { useTranslation } from '../data/useTranslation';

// Use relative URL in production, env variable in development
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper component to render test icon (white version for orange background)
const TestIcon = ({ iconKey, className = "w-12 h-12 sm:w-16 sm:h-16" }) => {
  if (iconKey === 'schwartz') {
    return <div className={`${className} text-white`}>{SchwartzIconWhite}</div>;
  }
  const iconUrl = TestIconsWhite[iconKey];
  if (iconUrl) {
    return <img src={iconUrl} alt={iconKey} className={`${className} object-contain`} />;
  }
  return null;
};

// Test sections configuration with detailed descriptions for APOLLO
const ALL_TEST_SECTIONS = [
  { 
    id: 'disc', 
    name: 'DISC', 
    icon: 'disc'
  },
  { 
    id: 'bigfive', 
    name: 'Big Five', 
    icon: 'bigfive'
  },
  { 
    id: 'via', 
    name: 'VIA Strengths', 
    icon: 'via'
  },
  { 
    id: 'schwartz', 
    name: 'Schwartz', 
    icon: 'schwartz'
  },
  { 
    id: 'enneagram', 
    name: 'Enneagram', 
    icon: 'enneagram'
  },
  { 
    id: 'mbti', 
    name: 'MBTI', 
    icon: 'mbti'
  },
];

function Assessment() {
  const { assessmentId } = useParams();
  const navigate = useNavigate();
  const { t, language } = useTranslation();
  const [searchParams] = useSearchParams();
  const [currentSection, setCurrentSection] = useState(0);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [questionnaires, setQuestionnaires] = useState(null);
  const [currentQuestions, setCurrentQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedTests, setSelectedTests] = useState([]);

  // Build test sections with translated names
  const ALL_TEST_SECTIONS_TRANSLATED = ALL_TEST_SECTIONS.map(s => ({
    ...s,
    name: t.tests[s.id]?.name || s.name,
  }));

  // Filter test sections based on selected tests
  const TEST_SECTIONS = ALL_TEST_SECTIONS_TRANSLATED.filter(section => 
    selectedTests.includes(section.id)
  );

  // Fetch questionnaires when language or searchParams change
  useEffect(() => {
    // Get selected tests from URL
    const testsParam = searchParams.get('tests');
    const requestedTests = testsParam
      ? testsParam.split(',')
      : ['disc', 'bigfive', 'via', 'schwartz', 'enneagram', 'mbti'];

    // Phase 2, verify pack-based authorization before loading
    // (server-side is already enforced; this catches URL tampering early for UX)
    const token = localStorage.getItem('bpm_access_token');
    if (token) {
      fetch(`${BACKEND_URL}/api/user/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          const authorized = data?.tests_authorized;
          if (Array.isArray(authorized)) {
            const unauthorized = requestedTests.filter(id => !authorized.includes(id));
            if (unauthorized.length > 0) {
              // eslint-disable-next-line no-alert
              alert(t.assessment.notAuthorizedToast);
              navigate('/dashboard', { replace: true, state: { unauthorized } });
              return;
            }
          }
          setSelectedTests(requestedTests);
          fetchQuestionnaires();
        })
        .catch(() => {
          // On network error, fall back to original behavior (backend will still enforce)
          setSelectedTests(requestedTests);
          fetchQuestionnaires();
        });
    } else {
      setSelectedTests(requestedTests);
      fetchQuestionnaires();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, language]);

  useEffect(() => {
    if (questionnaires && selectedTests.length > 0 && currentSection < TEST_SECTIONS.length) {
      const sectionId = TEST_SECTIONS[currentSection].id;
      setCurrentQuestions(questionnaires[sectionId]?.questions || []);
      setCurrentQuestionIndex(0); // Reset to first question of section
    }
  }, [currentSection, questionnaires, selectedTests]);

  const fetchQuestionnaires = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/questionnaires?lang=${language}`);
      if (response.ok) {
        const data = await response.json();
        setQuestionnaires(data);
      }
    } catch (error) {
      console.error('Error fetching questionnaires:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAnswer = (questionId, value, type = 'value') => {
    const sectionId = TEST_SECTIONS[currentSection].id;
    const key = `${sectionId}-${questionId}`;
    
    setAnswers(prev => ({
      ...prev,
      [key]: {
        ...(prev[key] || {}),  // Keep existing answer data
        question_id: questionId,
        test_name: sectionId,
        [type]: value
      }
    }));
  };

  // Save ONLY when a test is 100% complete - saves answers for current test only
  const saveCompletedTest = async (testId) => {
    // Get only answers for the completed test
    const testAnswers = Object.values(answers).filter(a => a.test_name === testId);
    
    if (testAnswers.length === 0) {
      throw new Error(`No answers found for test ${testId}`);
    }
    
    console.log(`Sending ${testAnswers.length} answers for test ${testId}...`);
    
    const token = localStorage.getItem('bpm_access_token');
    const response = await fetch(`${BACKEND_URL}/api/assessments/${assessmentId}/save-completed-test`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ 
        test_name: testId,
        answers: testAnswers 
      })
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`Error saving test ${testId}:`, errorText);
      throw new Error(`Failed to save test ${testId}: ${response.status}`);
    }
    
    const result = await response.json();
    console.log(`Test ${testId} saved successfully:`, result);
    return true;
  };

  const handleNext = async () => {
    // Check if we're at the last question of the section
    if (currentQuestionIndex < currentQuestions.length - 1) {
      // Move to next question in current section
      setCurrentQuestionIndex(prev => prev + 1);
      window.scrollTo(0, 0);
    } else {
      // Last question of section - TEST IS 100% COMPLETE
      const completedTestId = TEST_SECTIONS[currentSection].id;
      
      try {
        setSubmitting(true);
        console.log(`Saving completed test: ${completedTestId}...`);
        await saveCompletedTest(completedTestId);
        console.log(`Test ${completedTestId} saved successfully`);
        
        if (currentSection < TEST_SECTIONS.length - 1) {
          // Move to next test
          setCurrentSection(prev => prev + 1);
          window.scrollTo(0, 0);
        } else {
          // All selected tests completed - submit immediately
          console.log('All tests completed, submitting...');
          await submitAssessment();
        }
      } catch (error) {
        console.error('Error saving test:', error);
        alert('Erreur lors de la sauvegarde du test. Veuillez réessayer.');
      } finally {
        setSubmitting(false);
      }
    }
  };

  const handlePrevious = async () => {
    // Check if we're at the first question of the section
    if (currentQuestionIndex > 0) {
      // Move to previous question in current section
      setCurrentQuestionIndex(prev => prev - 1);
      window.scrollTo(0, 0);
    } else if (currentSection > 0) {
      // Going back to previous section - current test answers will be lost (not saved)
      // This is intentional: only 100% completed tests are saved
      setCurrentSection(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  const submitAssessment = async () => {
    setSubmitting(true);
    try {
      // Submit to calculate scores for all completed tests
      const token = localStorage.getItem('bpm_access_token');
      const response = await fetch(`${BACKEND_URL}/api/assessments/${assessmentId}/submit`, {
        method: 'POST',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        },
      });
      if (response.ok) {
        // Redirect to results page
        navigate(`/results/${assessmentId}`);
      }
    } catch (error) {
      console.error('Error submitting assessment:', error);
      alert('Erreur lors de la soumission. Veuillez réessayer.');
    } finally {
      setSubmitting(false);
    }
  };

  const isCurrentQuestionAnswered = () => {
    const sectionId = TEST_SECTIONS[currentSection].id;
    const question = currentQuestions[currentQuestionIndex];
    if (!question) return false;
    
    const key = `${sectionId}-${question.id}`;
    const answer = answers[key];
    
    // For DISC single_choice questions (51-70), only need most
    if (sectionId === 'disc' && question.single_choice) {
      return answer && answer.most !== undefined;
    }
    // For DISC most/least questions (1-50), need both most and least
    if (sectionId === 'disc') {
      return answer && answer.most !== undefined && answer.least !== undefined;
    }
    // For others, need value
    return answer && answer.value !== undefined;
  };

  // Calculate total progress across all SELECTED sections and questions
  const getTotalProgress = () => {
    let totalQuestions = 0;
    let answeredQuestions = 0;
    
    TEST_SECTIONS.forEach((section, sIdx) => {
      const sectionQuestions = questionnaires?.[section.id]?.questions || [];
      totalQuestions += sectionQuestions.length;
      
      if (sIdx < currentSection) {
        // All questions in previous sections are answered
        answeredQuestions += sectionQuestions.length;
      } else if (sIdx === currentSection) {
        // Current section - count up to current question
        answeredQuestions += currentQuestionIndex;
        if (isCurrentQuestionAnswered()) {
          answeredQuestions += 1;
        }
      }
    });
    
    return totalQuestions > 0 ? (answeredQuestions / totalQuestions) * 100 : 0;
  };

  const progress = getTotalProgress();

  if (loading || selectedTests.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto"></div>
          <p className="mt-4 text-white">{t.common.loading}</p>
        </div>
      </div>
    );
  }

  const section = TEST_SECTIONS[currentSection];
  const currentQuestion = currentQuestions[currentQuestionIndex];
  const testData = t.tests[section.id];
  
  if (!currentQuestion) {
    return null;
  }

  const key = `${section.id}-${currentQuestion.id}`;
  const answer = answers[key];
  const userName = localStorage.getItem('user_name') || 'User';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black text-white">
      {/* Header with Progress */}
      <div className="bg-gray-800 border-b border-gray-700 sticky top-0 z-10">
        <div className="container mx-auto px-4 py-3 sm:py-4">
          <div className="flex justify-between items-center mb-2 sm:mb-3">
            <div className="flex items-center">
              <span className="text-xl sm:text-2xl font-bold text-orange-500">BPM</span>
            </div>
            <span className="text-xs sm:text-sm text-gray-400 truncate max-w-[120px] sm:max-w-none">{userName}</span>
          </div>
          
          {/* Progress Bar */}
          <div className="w-full bg-gray-700 rounded-full h-2">
            <div 
              className="bg-orange-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress}%` }}
            />
          </div>
          <p className="text-xs sm:text-sm text-gray-400 mt-1 sm:mt-2">
            {testData?.name || section.name} - Q{currentQuestionIndex + 1}/{currentQuestions.length} - {Math.round(progress)}%
          </p>
        </div>
      </div>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-4 sm:py-8 max-w-4xl">
        {/* Section Header */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-xl p-4 sm:p-6 mb-4 sm:mb-8">
          <div className="flex items-start gap-3 sm:gap-4">
            <TestIcon iconKey={section.icon} className="w-12 h-12 sm:w-16 sm:h-16 flex-shrink-0" />
            <div className="flex-1">
              <h1 className="text-xl sm:text-2xl font-bold mb-1">{testData?.name || section.name}</h1>
              <p className="text-orange-100 text-sm sm:text-base font-medium mb-1">{testData?.description}</p>
              <p className="text-orange-200/80 text-xs sm:text-sm">{testData?.assessmentDescription}</p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-orange-400/30 flex justify-between items-center">
            <p className="text-xs sm:text-sm text-orange-200">
              {t.assessment.question} {currentQuestionIndex + 1} {t.assessment.of} {currentQuestions.length}
            </p>
            <p className="text-xs sm:text-sm text-orange-200 font-medium">
              {Math.round(progress)}% {t.assessment.completed}
            </p>
          </div>
        </div>

        {/* Single Question */}
        <div className="bg-gray-800 rounded-xl p-4 sm:p-8 border border-gray-700 min-h-[300px] sm:min-h-[400px]">
          <p className="text-lg sm:text-2xl mb-4 sm:mb-8 font-medium">
            <span className="text-orange-500 font-bold">Q{currentQuestionIndex + 1}.</span> {currentQuestion.text}
          </p>

          {/* DISC Format - Single Choice (Questions 51-70) */}
          {section.id === 'disc' && currentQuestion.options && currentQuestion.single_choice && (
            <div className="space-y-2 sm:space-y-3">
              <p className="text-xs sm:text-sm text-gray-400 mb-3 sm:mb-4 font-semibold">{t.assessment.chooseOne}</p>
              {currentQuestion.options.map((option, optIdx) => (
                <label 
                  key={`single-${optIdx}`} 
                  className="flex items-center space-x-3 p-3 sm:p-4 rounded-lg cursor-pointer hover:bg-gray-700 transition"
                >
                  <input
                    type="radio"
                    name={`${key}-single`}
                    checked={answer?.most === optIdx}
                    onChange={() => handleAnswer(currentQuestion.id, optIdx, 'most')}
                    className="w-5 h-5 text-orange-500"
                  />
                  <span className="text-sm sm:text-lg">{option}</span>
                </label>
              ))}
            </div>
          )}

          {/* DISC Format (Most/Least) - Questions 1-50 - Option A: Liste unique avec colonnes */}
          {section.id === 'disc' && currentQuestion.options && !currentQuestion.single_choice && (
            <div className="space-y-2">
              {/* Header */}
              <div className="flex items-center justify-end gap-2 sm:gap-4 mb-3 sm:mb-4 pr-2">
                <div className="flex items-center gap-1 sm:gap-2">
                  <span className="w-8 h-8 sm:w-10 sm:h-10 bg-green-600 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-lg">+</span>
                  <span className="text-xs sm:text-sm text-gray-400 hidden sm:inline">{t.assessment.mostLikeMe}</span>
                </div>
                <div className="flex items-center gap-1 sm:gap-2">
                  <span className="w-8 h-8 sm:w-10 sm:h-10 bg-red-600 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-lg">−</span>
                  <span className="text-xs sm:text-sm text-gray-400 hidden sm:inline">{t.assessment.leastLikeMe}</span>
                </div>
              </div>
              
              {/* Options list */}
              {currentQuestion.options.map((option, optIdx) => {
                const isSelectedMost = answer?.most === optIdx;
                const isSelectedLeast = answer?.least === optIdx;
                
                return (
                  <div 
                    key={optIdx}
                    className={`flex items-center justify-between p-3 sm:p-4 rounded-lg border transition ${
                      isSelectedMost ? 'border-green-500 bg-green-900/20' :
                      isSelectedLeast ? 'border-red-500 bg-red-900/20' :
                      'border-gray-700 bg-gray-800/50 hover:bg-gray-700/50'
                    }`}
                  >
                    <span className="text-sm sm:text-base flex-1 pr-2">{option}</span>
                    
                    <div className="flex items-center gap-2 sm:gap-4">
                      {/* Plus button */}
                      <button
                        type="button"
                        onClick={() => !isSelectedLeast && handleAnswer(currentQuestion.id, optIdx, 'most')}
                        disabled={isSelectedLeast}
                        className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-bold text-lg transition ${
                          isSelectedMost 
                            ? 'bg-green-600 text-white shadow-lg shadow-green-500/30' 
                            : isSelectedLeast
                            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                            : 'bg-gray-700 text-gray-300 hover:bg-green-600 hover:text-white'
                        }`}
                      >
                        +
                      </button>
                      
                      {/* Minus button */}
                      <button
                        type="button"
                        onClick={() => !isSelectedMost && handleAnswer(currentQuestion.id, optIdx, 'least')}
                        disabled={isSelectedMost}
                        className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center font-bold text-lg transition ${
                          isSelectedLeast 
                            ? 'bg-red-600 text-white shadow-lg shadow-red-500/30' 
                            : isSelectedMost
                            ? 'bg-gray-700 text-gray-500 cursor-not-allowed'
                            : 'bg-gray-700 text-gray-300 hover:bg-red-600 hover:text-white'
                        }`}
                      >
                        −
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Scale Format (1-5) */}
          {section.id !== 'disc' && (
            <div>
              <div className="flex justify-between items-center space-x-2 sm:space-x-3">
                {[1, 2, 3, 4, 5].map(value => (
                  <button
                    key={value}
                    onClick={() => handleAnswer(currentQuestion.id, value)}
                    className={`flex-1 py-3 sm:py-4 rounded-lg font-bold text-lg sm:text-xl transition ${
                      answer?.value === value
                        ? 'bg-orange-500 text-white shadow-lg scale-105'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {value}
                  </button>
                ))}
              </div>
              <div className="flex justify-between text-xs sm:text-sm text-gray-500 mt-2 sm:mt-4">
                <span>{t.assessment.notAtAll}</span>
                <span>{t.assessment.completely}</span>
              </div>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-4 sm:mt-8 pt-4 sm:pt-8 border-t border-gray-700 pb-20 sm:pb-4">
          <button
            onClick={handlePrevious}
            disabled={currentSection === 0 && currentQuestionIndex === 0}
            className="px-4 sm:px-6 py-2 sm:py-3 bg-gray-700 hover:bg-gray-600 rounded-lg font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
          >
            ← {t.common.previous}
          </button>
          
          <button
            onClick={handleNext}
            disabled={!isCurrentQuestionAnswered() || submitting}
            className="px-4 sm:px-6 py-2 sm:py-3 bg-orange-500 hover:bg-orange-600 rounded-lg font-semibold transition disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base"
          >
            {submitting ? (
              t.assessment.processing
            ) : currentSection === TEST_SECTIONS.length - 1 && currentQuestionIndex === currentQuestions.length - 1 ? (
              selectedTests.length >= 6 ? t.assessment.viewResults : t.assessment.saveAnswers
            ) : (
              `${t.common.next} →`
            )}
          </button>
        </div>
      </main>
    </div>
  );
}

export default Assessment;
